import { api } from '../../api/endpoints.js';
import { Badge, Card, DataTable, Input, Pagination, Select } from '../../components/ui.jsx';
import { useListQuery } from '../../hooks/useListQuery.js';
import { date, humanise, number } from '../../lib/format.js';

/**
 * What each kind of movement is, in the words an admin would use.
 *
 * The stored values are terse on purpose — they are a contract with the apps —
 * so the readable version lives here rather than being derived by replacing
 * underscores, which would give "Ad post" for something everyone calls an
 * ad.
 */
const REASONS = {
  purchase: { label: 'Top-up', tone: 'success' },
  promotional: { label: 'Free credits', tone: 'info' },
  refund: { label: 'Refund', tone: 'success' },
  admin_adjustment: { label: 'Adjustment', tone: 'warning' },
  expiry: { label: 'Expired', tone: 'neutral' },
  ad_post: { label: 'Ad', tone: 'neutral' },
  ad_renewal: { label: 'Ad renewal', tone: 'neutral' },
  boost: { label: 'Boost', tone: 'info' },
  contact_unlock: { label: 'Contact unlocked', tone: 'neutral' },
  lead_unlock: { label: 'Lead unlocked', tone: 'neutral' },
  enquiry_unlock: { label: 'Enquiry unlocked', tone: 'neutral' },
  featured_profile: { label: 'Featured profile', tone: 'info' },
};

/*
 * Every reason the backend writes has to be in the map above, not just the
 * ones with a pretty label: the filter is built from its keys, so a missing
 * type is a spend nobody can search the ledger for.
 */

/** Credits, signed. A spend reads as a spend without having to check a column. */
function Amount({ value }) {
  const positive = value > 0;

  return (
    <span className={`nowrap ${positive ? 'credits--in' : 'credits--out'}`}>
      {positive ? '+' : '−'}
      {number(Math.abs(value))}
    </span>
  );
}

export default function LedgerTab() {
  const list = useListQuery({
    key: 'admin-credit-ledger',
    fetcher: api.credits.ledger,
    initialFilters: { type: '' },
    searchable: true,
  });

  const columns = [
    {
      key: 'when',
      header: 'When',
      render: (row) => <span className="table__muted nowrap">{date(row.createdAt, { withTime: true })}</span>,
    },
    {
      key: 'reason',
      header: 'Reason',
      render: (row) => {
        const reason = REASONS[row.type];
        return <Badge tone={reason?.tone || 'neutral'} label={reason?.label || humanise(row.type)} />;
      },
    },
    {
      key: 'description',
      header: 'Detail',
      render: (row) => <span className="truncate">{row.description || '—'}</span>,
    },
    {
      key: 'who',
      header: 'Taken by',
      render: (row) => <span className="table__muted">{humanise(row.performedByType)}</span>,
    },
    {
      key: 'amount',
      header: 'Credits',
      className: 'table__numeric',
      render: (row) => <Amount value={row.amount} />,
    },
    {
      key: 'balance',
      header: 'Balance after',
      className: 'table__numeric',
      render: (row) => <span className="table__muted">{number(row.balanceAfter)}</span>,
    },
  ];

  return (
    <Card bodyless>
      <div className="card__body">
        <div className="toolbar">
          <div className="toolbar__search">
            <Input
              placeholder="Search by name or phone"
              value={list.search}
              onChange={(event) => list.setSearch(event.target.value)}
              aria-label="Search the ledger"
            />
          </div>

          <Select
            value={list.filters.type}
            onChange={(event) => list.setFilter('type', event.target.value)}
            aria-label="Filter by reason"
          >
            <option value="">Any reason</option>
            {Object.entries(REASONS).map(([value, reason]) => (
              <option key={value} value={value}>
                {reason.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={list.rows}
        query={list.query}
        empty={{
          title: 'Nothing here',
          body: 'No credit movement matches these filters.',
        }}
      />
      <Pagination meta={list.meta} page={list.page} onPageChange={list.setPage} />
    </Card>
  );
}
